/**
 * A menu pasted in as text, turned into the rows the importer already understands.
 *
 * The motion this is for: somebody with the venue's PDF open on one screen and Verlo on the
 * other. They can already select all and copy, and a menu copied out of a PDF arrives as
 * one dish per line with the price on the end. So take the paste rather than asking them
 * for a spreadsheet of a document they have in their hand.
 *
 * Deliberately four rules and no cleverness. The output goes through the same review table
 * as a spreadsheet — every row visible, every row removable, nothing written until the
 * button — so the cost of a wrong guess is that somebody unticks a line, and the cost of a
 * model would be an API key, a bill per import and no way to tell a misread from a typo.
 */

//: A trailing price: optional currency, digits, optional decimals with . or , as the
//: separator. `£9.50`, `9.50`, `9,50`, `£12`, and the dot leaders menus love
//: (`Wings ....... £9.50`) all land here.
//:
//: The digits have to be set off from the name: by a currency symbol, a space or a leader.
//: Without that "Wings x6" was a dish called "Wings x" at £6. Group 1 is the currency
//: symbol, when there is one.
const PRICE = /(?:([£$€])\s*|(?:^|[\s.…])[.…\s]*)(\d{1,4}(?:[.,]\d{1,2})?)\s*$/;

//: Four digits with no currency and no pence is a year, not a price: "Pinot Grigio 2019"
//: is a wine waiting for its price on the next line, not a £2,019 bottle.
const LOOKS_LIKE_A_YEAR = (m) => !m[1] && /^\d{4}$/.test(m[2]);

//: A line long enough, and sentence-shaped enough, to be a dish's description rather than
//: the next section heading. Menus set descriptions under the dish in a smaller face; they
//: arrive in the paste as an ordinary line with no price.
const LOOKS_LIKE_PROSE = (s) => s.length > 32 || /[,;]/.test(s);

//: A line set the way menus set headings: in capitals ("SMALL PLATES"), a word or two
//: ("Mains", "Soft drinks", "Beer & cider"), or with a capital on every word that carries
//: one in a title ("From the Grill", "Burgers & Hot Dogs"). A description is a sentence
//: ("Baked here every morning"): one capital, at the front.
const SMALL_WORD = /^(&|a|an|and|at|by|for|from|in|of|on|or|the|to|with)$/i;
const HEADING_SHAPED = (s) => {
  const words = s.split(/\s+/);
  const titled = words.filter((w) => !SMALL_WORD.test(w));
  return (/[A-Z]/.test(s) && s === s.toUpperCase())
    || words.filter((w) => !/^(&|and)$/i.test(w)).length <= 2
    || (titled.length > 0 && titled.every((w) => /^[^a-z]/.test(w)));
};

const clean = (s) => s.replace(/[.…\s]+$/, '').replace(/^[-•*\s]+/, '').trim();

/**
 * `{ rows, sections }` — rows in the shape `menu`'s column mapping expects, so the CSV built
 * from them maps itself.
 */
export function parseMenu(text) {
  const rows = [];
  let section = '';
  let gap = true;                              // a blank line since the last dish

  for (const raw of String(text || '').split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) { gap = true; continue; }       // 1. blank lines are nothing, but they separate

    const m = line.match(PRICE);
    if (m && !LOOKS_LIKE_A_YEAR(m)) {          // 2. a trailing price makes it a dish
      const name = clean(line.slice(0, m.index));
      if (!name) continue;                     // a bare price belongs to nothing
      rows.push({ name, price: m[2].replace(',', '.'), category: section, description: '' });
      gap = false;
      continue;
    }

    // 3/4. No price. It is the dish's description if there is a dish above it without one and
    // either it reads like prose, or it sits tight under the dish with no blank line between
    // and isn't shaped like a heading ("Baked here every morning"). Otherwise it is the next
    // section heading: headings come after a gap, in capitals, in a word or two, or titled.
    const last = rows[rows.length - 1];
    if (last && !last.description && (LOOKS_LIKE_PROSE(line) || (!gap && !HEADING_SHAPED(line)))) {
      last.description = clean(line);
    } else {
      section = clean(line);
    }
  }

  return { rows, sections: [...new Set(rows.map((r) => r.category).filter(Boolean))] };
}

const cell = (v) => {
  const s = String(v ?? '');
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/**
 * The parsed rows as a CSV file, headed with names `auto_map_columns` already knows.
 *
 * This is what makes the whole feature a frontend change: the file goes to the *existing*
 * `/bridge/imports/upload`, so the mapping, the validation, the row documents, the review
 * table and the commit are all untouched and already tested.
 */
export function menuCsvFile(rows, filename = 'pasted-menu.csv') {
  const head = ['Name', 'Price', 'Section', 'Description'];
  const body = rows.map((r) => [r.name, r.price, r.category, r.description].map(cell).join(','));
  return new File([[head.join(','), ...body].join('\n')], filename, { type: 'text/csv' });
}
